// EodSettlementMonitor
//
// 90-minute late-session visualization:
// - SPX history is seeded from hydrated indexData.SPX minute closes.
// - After connection, every streaming SPX update is appended using the
//   aggregationState.SPX.lastTickTime timestamp.
// - EOD Market Intelligence posts define discrete Support / Resistance rails.
// - Rails are valid only until the next EOD post.
// - If Support === Resistance, separate rails disappear and a +/-10 point
//   overlap zone is highlighted ONLY for that interval.
// - If the next post separates the strikes, the overlap zone ends immediately
//   and the support/resistance rails move back apart.
// - Missing Support or Resistance remains missing; no level is invented.

import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  WidgetPanelControls,
  WidgetPanelLiveStatus,
  WidgetPanelStatus,
} from '../Dashboard/WidgetPanelSlot';
import useWebSocketDataStore from '../../Store/WebSocketDataStore';
import useGreeksWebsocketDataStore from '../../Store/GreeksWebsocketDataStore';
import usePlaybackStore from '../../Store/playbackStore';
import { useChartTimeWindowStore } from '../../Store/chartTimeWindowStore';
import useWidgetMinuteFocusStore, {
  useFocusedMinuteSourceCleanup,
} from '../../Store/widgetMinuteFocusStore';
import {
  getPostTimestamp,
  useMarketIntelligenceStore,
} from '../../Store/marketIntelligenceStore';
import { focusedPointIndex } from '../../Utils/widgetMinuteFocus';
import {
  PLOT_PAN_THRESHOLD_PX,
  WINDOW_MODE,
} from '../../Utils/chartTimeWindow';
import {
  denseSeriesGapRanges,
  forwardFillSeriesAtGaps,
  mergeSeriesGapRanges,
  replaySeriesGapRanges,
} from '../../Utils/seriesPresentation';
import useGexSharedTimeWindow from './useGexSharedTimeWindow';
import '../../Styles/EODSettlementMonitor.css';

const ET_TIMEZONE = 'America/New_York';

const EOD_START_HOUR = 14;
const EOD_START_MINUTE = 30;
const EOD_END_HOUR = 16;
const EOD_END_MINUTE = 0;

const OVERLAP_DISTANCE_PTS = 10;

// GTS is stored as an absolute magnitude plus a separate trend_type.
// 0.20 is the first significance level used by GEX Trend Monitor. Keep this
// isolated so the EOD interpretation threshold can be tuned independently.
const GTS_CHALLENGE_THRESHOLD = 0.30;
const GTS_MAX_CONTEXT = 0.70;

const ML = 8;
const MR = 44;
const MT = 8;
const MB = 18;

const EMPTY_ARRAY = [];

const TICK_MINUTES = new Set(['00', '15', '30', '45']);

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function scaleLinear([d0, d1], [r0, r1]) {
  return (value) => (
    d1 === d0
      ? r0
      : r0 + ((value - d0) / (d1 - d0)) * (r1 - r0)
  );
}

function eodVisibleRange(sharedRange, eodWindow, preferenceMode, minSpanMs) {
  const windowFrom = Number(eodWindow?.from);
  const windowTo = Number(eodWindow?.to);
  const sharedFrom = Number(sharedRange?.from);
  const sharedTo = Number(sharedRange?.to);

  if (!(windowTo > windowFrom)) return null;
  if (!(sharedTo > sharedFrom)) {
    return { from: windowFrom, to: windowTo, outside: false };
  }
  if (sharedTo <= windowFrom || sharedFrom >= windowTo) {
    return { from: windowFrom, to: windowTo, outside: true };
  }

  let from = Math.max(windowFrom, sharedFrom);
  let to = preferenceMode === WINDOW_MODE.ABSOLUTE
    ? Math.min(windowTo, sharedTo)
    : windowTo;
  const minimumSpan = Math.min(
    Math.max(1, Number(minSpanMs) || 1),
    windowTo - windowFrom,
  );

  if (to - from < minimumSpan) {
    if (from <= windowFrom) {
      to = Math.min(windowTo, windowFrom + minimumSpan);
    } else {
      from = Math.max(windowFrom, to - minimumSpan);
    }
  }

  return { from, to, outside: false };
}

function getEtParts(timestamp) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ET_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(timestamp));

  const out = {};

  parts.forEach(({ type, value }) => {
    if (type !== 'literal') out[type] = value;
  });

  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    hour: Number(out.hour),
    minute: Number(out.minute),
    second: Number(out.second),
  };
}

function getEtDateKey(timestamp) {
  const parts = getEtParts(timestamp);

  return [
    String(parts.year).padStart(4, '0'),
    String(parts.month).padStart(2, '0'),
    String(parts.day).padStart(2, '0'),
  ].join('-');
}

// Convert an ET wall-clock time into epoch milliseconds without assuming the
// browser itself is in ET. The correction loop also handles EST/EDT changes.
function etWallTimeToEpoch(dateKey, hour, minute) {
  const [year, month, day] = String(dateKey)
    .split('-')
    .map(Number);

  if (
    !Number.isFinite(year)
    || !Number.isFinite(month)
    || !Number.isFinite(day)
  ) {
    return NaN;
  }

  const targetWallUtc = Date.UTC(
    year,
    month - 1,
    day,
    hour,
    minute,
    0,
    0,
  );

  let guess = targetWallUtc + (4 * 60 * 60 * 1000);

  for (let pass = 0; pass < 3; pass += 1) {
    const actual = getEtParts(guess);
    const actualWallUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second,
      0,
    );

    guess += targetWallUtc - actualWallUtc;
  }

  return guess;
}

function parseEpoch(value) {
  if (value == null) return null;

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }

  const numeric = Number(value);

  if (Number.isFinite(numeric)) {
    return numeric < 1e12 ? numeric * 1000 : numeric;
  }

  const parsed = Date.parse(value);

  return Number.isFinite(parsed) ? parsed : null;
}

function parseStrike(value) {
  if (value == null || value === '') return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const cleaned = String(value)
    .trim()
    .replaceAll(',', '');

  // The redesigned EOD program uses one strike. Deliberately reject old broad
  // ranges instead of silently converting them into a synthetic level.
  if (
    cleaned.includes('-')
    || cleaned.includes('–')
    || cleaned.includes('—')
  ) {
    return null;
  }

  const parsed = Number(cleaned);

  return Number.isFinite(parsed) ? parsed : null;
}

function fmtPrice(value) {
  return Number.isFinite(value) ? Number(value).toFixed(2) : '--';
}

function fmtStrike(value) {
  if (!Number.isFinite(value)) return '--';

  return Number.isInteger(value)
    ? String(value)
    : Number(value).toFixed(2).replace(/\.?0+$/, '');
}

function fmtAxisPrice(value) {
  if (!Number.isFinite(value)) return '';

  const rounded = Math.round(value * 2) / 2;

  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(1);
}

function fmtTime(timestamp) {
  if (!Number.isFinite(timestamp)) return '';

  try {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      timeZone: ET_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return '';
  }
}

function fmtAxisTime(timestamp) {
  const full = fmtTime(timestamp);

  return full ? full.slice(0, 5) : '';
}

function postKeyLevels(post) {
  return (
    post?.key_levels
    || post?.keyLevels
    || post?.display_payload?.key_levels
    || post?.displayPayload?.keyLevels
    || {}
  );
}

function sourceModule(post) {
  return post?.source_module || post?.sourceModule || null;
}

function latestDateKeyFromBars(bars = []) {
  for (let index = bars.length - 1; index >= 0; index -= 1) {
    const t = parseEpoch(bars[index]?.time ?? bars[index]?.t);
    if (Number.isFinite(t)) return getEtDateKey(t);
  }

  return null;
}

function latestDateKeyFromPosts(posts = []) {
  for (let index = posts.length - 1; index >= 0; index -= 1) {
    const t = parseEpoch(getPostTimestamp(posts[index]));
    if (Number.isFinite(t)) return getEtDateKey(t);
  }

  return null;
}

function normalizeGtsTrendType(value) {
  const normalized = String(value || '').trim().toLowerCase();

  if (normalized === 'call' || normalized === 'bullish') {
    return 'call';
  }

  if (normalized === 'put' || normalized === 'bearish') {
    return 'put';
  }

  return 'neutral';
}

function buildGtsSeries(rows = [], tradeDateKey, sessionStart, sessionEnd) {
  return rows
    .map((row) => {
      const t = parseEpoch(row?.timestamp ?? row?.formatted_date);
      const value = Math.abs(Number(row?.value));
      const side = normalizeGtsTrendType(row?.trend_type);

      if (
        !Number.isFinite(t)
        || !Number.isFinite(value)
        || side === 'neutral'
        || getEtDateKey(t) !== tradeDateKey
        || t < sessionStart
        || t > sessionEnd
      ) {
        return null;
      }

      return {
        t,
        value,
        side,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.t - b.t);
}

function gtsContextLabel(gts, update) {
  if (!gts) return 'GTS --';

  const sideLabel = gts.side === 'call' ? 'CALL' : 'PUT';
  const valueLabel = gts.value.toFixed(2);

  if (gts.value < GTS_CHALLENGE_THRESHOLD) {
    return `GTS ${sideLabel} ${valueLabel} • LOW`;
  }

  if (gts.side === 'call') {
    return Number.isFinite(update?.resistance)
      ? `GTS CALL ${valueLabel} • RESISTANCE CHALLENGE`
      : `GTS CALL ${valueLabel} • NO CALL LEVEL`;
  }

  return Number.isFinite(update?.support)
    ? `GTS PUT ${valueLabel} • SUPPORT CHALLENGE`
    : `GTS PUT ${valueLabel} • NO PUT LEVEL`;
}

function currentStateLabel(update) {
  if (!update) return '';

  if (
    Number.isFinite(update.support)
    && Number.isFinite(update.resistance)
    && update.support === update.resistance
  ) {
    return `OVERLAP ${fmtStrike(update.support)} ±${OVERLAP_DISTANCE_PTS}`;
  }

  if (
    Number.isFinite(update.support)
    && Number.isFinite(update.resistance)
  ) {
    return `S ${fmtStrike(update.support)}  /  R ${fmtStrike(update.resistance)}`;
  }

  if (Number.isFinite(update.support)) {
    return `S ${fmtStrike(update.support)}  /  R --`;
  }

  if (Number.isFinite(update.resistance)) {
    return `S --  /  R ${fmtStrike(update.resistance)}`;
  }

  return '';
}

function railStateTone(update) {
  if (!update) return 'neutral';

  if (
    Number.isFinite(update.support)
    && Number.isFinite(update.resistance)
    && update.support === update.resistance
  ) {
    return 'overlap';
  }

  if (
    Number.isFinite(update.support)
    && Number.isFinite(update.resistance)
  ) {
    return 'range';
  }

  return 'one-sided';
}

export default function EodSettlementMonitor() {
  const historicalBars = useWebSocketDataStore(
    (state) => state.indexData?.SPX ?? EMPTY_ARRAY,
  );

  const liveSpx = useWebSocketDataStore(
    (state) => state.aggregationState?.SPX ?? null,
  );

  const replayActive = useWebSocketDataStore(
    (state) => state.replayActive,
  );
  const liveIndexGaps = useWebSocketDataStore(
    (state) => state.liveGapRanges ?? EMPTY_ARRAY,
  );

  const recentPosts = useMarketIntelligenceStore(
    (state) => state.recentFrontendPosts ?? EMPTY_ARRAY,
  );

  const gtsRows = useGreeksWebsocketDataStore(
    (state) => state.gexTrendStrength?.spx ?? EMPTY_ARRAY,
  );
  const liveGreekGaps = useGreeksWebsocketDataStore((state) => state.liveGapRanges || EMPTY_ARRAY);
  const playbackMode = usePlaybackStore((state) => state.mode);
  const replayCoverage = usePlaybackStore((state) => state.replayCoverage);
  const replayCutoffMs = usePlaybackStore((state) => state.replayCutoffMs);

  const [clockNow, setClockNow] = useState(Date.now());
  const [liveTicks, setLiveTicks] = useState([]);
  const [liveTickDateKey, setLiveTickDateKey] = useState(null);
  const [hover, setHover] = useState(null);
  const [sharedRangeEntry, setSharedRangeEntry] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const sharedWindowPreference = useChartTimeWindowStore((state) => state.preference);
  const focusedMinuteMs = useWidgetMinuteFocusStore((state) => state.minuteMs);
  const focusSourceId = useWidgetMinuteFocusStore((state) => state.sourceId);
  const setFocusedMinute = useWidgetMinuteFocusStore((state) => state.setFocusedMinute);
  const clearFocusedMinute = useWidgetMinuteFocusStore((state) => state.clearFocusedMinute);
  useFocusedMinuteSourceCleanup('eodRangeInsights');
  const [size, setSize] = useState({ w: 420, h: 180 });

  const resizeObserverRef = useRef(null);
  const panRef = useRef(null);
  const viewDetachedRef = useRef(false);
  const clipId = useId().replace(/:/g, '');

  const wrapRef = useCallback((node) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    if (!node) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;

      setSize({
        w: Math.max(1, Math.round(width)),
        h: Math.max(1, Math.round(height)),
      });
    });

    observer.observe(node);
    resizeObserverRef.current = observer;
  }, []);

  useEffect(() => {
    const id = window.setInterval(
      () => setClockNow(Date.now()),
      15000,
    );

    return () => window.clearInterval(id);
  }, []);

  const eodPostsAll = useMemo(
    () => recentPosts.filter(
      (post) => sourceModule(post) === 'eod_range',
    ),
    [recentPosts],
  );

  const currentEtDateKey = getEtDateKey(clockNow);

  // Loaded bars own the rendered session. Empty live data stays on today so a
  // stale EOD post cannot select an old session after replay exits.
  const tradeDateKey = useMemo(() => {
    const barDateKey = latestDateKeyFromBars(historicalBars);
    if (barDateKey) return barDateKey;
    if (!replayActive) return currentEtDateKey;

    return latestDateKeyFromPosts(eodPostsAll) || currentEtDateKey;
  }, [
    currentEtDateKey,
    historicalBars,
    eodPostsAll,
    replayActive,
  ]);
  const sharedRange = (
    sharedRangeEntry?.tradeDateKey === tradeDateKey
      ? sharedRangeEntry.range
      : null
  );

  useEffect(() => {
    setSharedRangeEntry((entry) => (
      entry?.tradeDateKey === tradeDateKey ? entry : null
    ));
  }, [tradeDateKey]);

  const sessionStart = useMemo(
    () => etWallTimeToEpoch(
      tradeDateKey,
      EOD_START_HOUR,
      EOD_START_MINUTE,
    ),
    [tradeDateKey],
  );

  const sessionEnd = useMemo(
    () => etWallTimeToEpoch(
      tradeDateKey,
      EOD_END_HOUR,
      EOD_END_MINUTE,
    ),
    [tradeDateKey],
  );

  const gtsSeries = useMemo(
    () => buildGtsSeries(
      gtsRows,
      tradeDateKey,
      sessionStart,
      sessionEnd,
    ).filter((point) => (
      !replayActive
      || !Number.isFinite(replayCutoffMs)
      || point.t <= replayCutoffMs
    )),
    [
      gtsRows,
      tradeDateKey,
      sessionStart,
      sessionEnd,
      replayActive,
      replayCutoffMs,
    ],
  );

  const liveTickTime = parseEpoch(
    liveSpx?.lastTickTime,
  );
  const liveTickPrice = Number(liveSpx?.close);

  const navigationSeries = useMemo(() => {
    const byTime = new Map();

    historicalBars.forEach((bar) => {
      const t = parseEpoch(bar?.time ?? bar?.t);
      if (Number.isFinite(t) && getEtDateKey(t) === tradeDateKey) {
        byTime.set(t, { t });
      }
    });

    if (
      Number.isFinite(liveTickTime)
      && getEtDateKey(liveTickTime) === tradeDateKey
    ) {
      byTime.set(liveTickTime, { t: liveTickTime });
    }

    return [...byTime.values()].sort((a, b) => a.t - b.t);
  }, [historicalBars, liveTickTime, tradeDateKey]);

  const setSharedViewRange = useCallback((range) => {
    setSharedRangeEntry({
      tradeDateKey,
      range: { from: range.start, to: range.end },
    });
  }, [tradeDateKey]);
  const {
    domainMs,
    minSpanMs,
    schedulePublish,
  } = useGexSharedTimeWindow({
    series: navigationSeries,
    sourceId: 'eod-range-insights',
    setViewRange: setSharedViewRange,
    viewDetachedRef,
  });

  useEffect(() => {
    if (
      !Number.isFinite(liveTickTime)
      || !Number.isFinite(liveTickPrice)
      || liveTickTime < sessionStart
      || liveTickTime > sessionEnd
      || getEtDateKey(liveTickTime) !== tradeDateKey
    ) {
      return;
    }

    const tickDateKey = getEtDateKey(liveTickTime);

    setLiveTicks((previous) => {
      const sameSession = liveTickDateKey === tickDateKey;
      const working = sameSession ? previous : [];
      const last = working[working.length - 1];

      if (last?.t === liveTickTime) {
        if (last.price === liveTickPrice) return working;

        return [
          ...working.slice(0, -1),
          {
            t: liveTickTime,
            price: liveTickPrice,
          },
        ];
      }

      return [
        ...working,
        {
          t: liveTickTime,
          price: liveTickPrice,
        },
      ];
    });

    if (liveTickDateKey !== tickDateKey) {
      setLiveTickDateKey(tickDateKey);
    }
  }, [
    liveTickTime,
    liveTickPrice,
    sessionStart,
    sessionEnd,
    tradeDateKey,
    liveTickDateKey,
  ]);

  useEffect(() => {
    if (
      liveTickDateKey
      && liveTickDateKey !== tradeDateKey
    ) {
      setLiveTicks([]);
      setLiveTickDateKey(null);
    }
  }, [tradeDateKey, liveTickDateKey]);

  const eodUpdates = useMemo(() => (
    eodPostsAll
      .map((post) => {
        const t = parseEpoch(getPostTimestamp(post));

        if (
          !Number.isFinite(t)
          || getEtDateKey(t) !== tradeDateKey
          || t < sessionStart
          || t > sessionEnd
          || (
            replayActive
            && Number.isFinite(replayCutoffMs)
            && t > replayCutoffMs
          )
        ) {
          return null;
        }

        const levels = postKeyLevels(post);

        return {
          id: post?.id || `${sourceModule(post)}:${t}`,
          t,
          support: parseStrike(
            levels.Support ?? levels.support,
          ),
          resistance: parseStrike(
            levels.Resistance ?? levels.resistance,
          ),
          post,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.t - b.t)
  ), [
    eodPostsAll,
    tradeDateKey,
    sessionStart,
    sessionEnd,
    replayActive,
    replayCutoffMs,
  ]);

  const firstLiveTickTime = liveTicks[0]?.t ?? null;
  const firstLiveMinuteStart = Number.isFinite(firstLiveTickTime)
    ? Math.floor(firstLiveTickTime / 60000) * 60000
    : null;

  const historicalPricePoints = useMemo(() => (
    historicalBars
      .map((bar) => ({
        t: parseEpoch(bar?.time ?? bar?.t),
        price: Number(bar?.close ?? bar?.c),
      }))
      .filter((point) => (
        Number.isFinite(point.t)
        && Number.isFinite(point.price)
        && getEtDateKey(point.t) === tradeDateKey
        && point.t >= sessionStart
        && point.t <= sessionEnd
        && (
          !Number.isFinite(firstLiveMinuteStart)
          || point.t < firstLiveMinuteStart
        )
      ))
      .sort((a, b) => a.t - b.t)
  ), [
    historicalBars,
    tradeDateKey,
    sessionStart,
    sessionEnd,
    firstLiveMinuteStart,
  ]);

  const pricePoints = useMemo(() => {
    const merged = [
      ...historicalPricePoints,
      ...liveTicks.filter(
        (point) => (
          point.t >= sessionStart
          && point.t <= sessionEnd
          && getEtDateKey(point.t) === tradeDateKey
        ),
      ),
    ];

    return merged
      .filter((point) => (
        !replayActive
        || !Number.isFinite(replayCutoffMs)
        || point.t <= replayCutoffMs
      ))
      .sort((a, b) => a.t - b.t);
  }, [
    historicalPricePoints,
    liveTicks,
    tradeDateKey,
    sessionStart,
    sessionEnd,
    replayActive,
    replayCutoffMs,
  ]);

  const replaying = playbackMode === 'playing';
  const priceGaps = useMemo(() => mergeSeriesGapRanges(
    replaying
      ? replaySeriesGapRanges(replayCoverage, ['index:SPX'], replayCutoffMs)
      : liveIndexGaps,
    denseSeriesGapRanges(pricePoints, { source: 'index:SPX' }),
  ), [liveIndexGaps, pricePoints, replayCoverage, replayCutoffMs, replaying]);
  const gtsGaps = useMemo(() => mergeSeriesGapRanges(
    replaying
      ? replaySeriesGapRanges(replayCoverage, ['greeks:gexTrend'], replayCutoffMs)
      : liveGreekGaps,
    denseSeriesGapRanges(gtsSeries, { source: 'greeks:gexTrend' }),
  ), [gtsSeries, liveGreekGaps, replayCoverage, replayCutoffMs, replaying]);
  const displayPricePoints = useMemo(
    () => forwardFillSeriesAtGaps(pricePoints, priceGaps),
    [priceGaps, pricePoints],
  );
  const displayGtsSeries = useMemo(
    () => forwardFillSeriesAtGaps(gtsSeries, gtsGaps),
    [gtsGaps, gtsSeries],
  );

  const sessionNow = replayActive
    ? (
        Number.isFinite(liveTickTime)
          ? liveTickTime
          : displayPricePoints[displayPricePoints.length - 1]?.t ?? sessionStart
      )
    : clockNow;

  const chartThrough = clamp(
    replayActive && Number.isFinite(replayCutoffMs)
      ? replayCutoffMs
      : sessionNow,
    sessionStart,
    sessionEnd,
  );

  const currentUpdate = useMemo(() => {
    let current = null;

    for (const update of eodUpdates) {
      if (update.t <= chartThrough) current = update;
      else break;
    }

    return current;
  }, [eodUpdates, chartThrough]);

  const currentGts = useMemo(() => {
    let current = null;

    for (const point of displayGtsSeries) {
      if (point.t <= chartThrough) current = point;
      else break;
    }

    return current;
  }, [
    chartThrough,
    displayGtsSeries,
  ]);

  const intervals = useMemo(() => (
    eodUpdates.map((update, index) => {
      const next = eodUpdates[index + 1];
      const end = Math.min(
        next?.t ?? chartThrough,
        chartThrough,
        sessionEnd,
      );

      if (end <= update.t) return null;

      const overlap = (
        Number.isFinite(update.support)
        && Number.isFinite(update.resistance)
        && update.support === update.resistance
      );

      return {
        ...update,
        end,
        overlap,
      };
    }).filter(Boolean)
  ), [
    eodUpdates,
    chartThrough,
    sessionEnd,
  ]);

  const gtsIntervals = useMemo(() => (
    displayGtsSeries.map((point, index) => {
      const next = displayGtsSeries[index + 1];
      const end = Math.min(
        next?.t ?? chartThrough,
        chartThrough,
        sessionEnd,
      );

      if (end <= point.t) return null;

      return {
        ...point,
        end,
      };
    }).filter(Boolean)
  ), [
    displayGtsSeries,
    chartThrough,
    sessionEnd,
  ]);

  // GTS is directional context, not a blanket "range challenge":
  // CALL GTS can challenge only the call-side Resistance rail.
  // PUT GTS can challenge only the put-side Support rail.
  // GTS never moves or invents an EOD level.
  const gtsChallengeSegments = useMemo(() => {
    const segments = [];

    gtsIntervals.forEach((gts) => {
      if (gts.value < GTS_CHALLENGE_THRESHOLD) return;

      intervals.forEach((rail) => {
        const start = Math.max(gts.t, rail.t);
        const end = Math.min(gts.end, rail.end);

        if (end <= start) return;

        if (
          gts.side === 'call'
          && Number.isFinite(rail.resistance)
        ) {
          segments.push({
            key: `call:${start}:${end}:${rail.id}`,
            side: 'call',
            t: start,
            end,
            level: rail.resistance,
            overlap: rail.overlap,
            value: gts.value,
          });
        }

        if (
          gts.side === 'put'
          && Number.isFinite(rail.support)
        ) {
          segments.push({
            key: `put:${start}:${end}:${rail.id}`,
            side: 'put',
            t: start,
            end,
            level: rail.support,
            overlap: rail.overlap,
            value: gts.value,
          });
        }
      });
    });

    return segments;
  }, [
    gtsIntervals,
    intervals,
  ]);

  const sessionClockMs = replayActive ? replayCutoffMs : clockNow;
  const isBeforeWindow = Number.isFinite(sessionClockMs) && sessionClockMs < sessionStart;
  const isAfterWindow = Number.isFinite(sessionClockMs) && sessionClockMs > sessionEnd;
  const eodWindow = { from: sessionStart, to: sessionEnd };
  const preferenceMode = sharedWindowPreference?.mode ?? WINDOW_MODE.LATEST;
  const displayPreferenceMode = isPanning && panRef.current
    ? panRef.current.preferenceMode
    : preferenceMode;
  const visibleRange = eodVisibleRange(
    sharedRange,
    eodWindow,
    displayPreferenceMode,
    minSpanMs,
  ) ?? eodWindow;
  const canPan = !visibleRange.outside
    && visibleRange.to - visibleRange.from < sessionEnd - sessionStart - 1;

  const { w: VW, h: VH } = size;
  const plotWidth = Math.max(1, VW - ML - MR);
  const plotHeight = Math.max(1, VH - MT - MB);

  const xFn = scaleLinear(
    [visibleRange.from, visibleRange.to],
    [ML, VW - MR],
  );

  const yValues = [];

  pricePoints
    .filter((point) => point.t <= chartThrough)
    .forEach((point) => yValues.push(point.price));

  intervals.forEach((interval) => {
    if (Number.isFinite(interval.support)) {
      yValues.push(interval.support);
    }

    if (Number.isFinite(interval.resistance)) {
      yValues.push(interval.resistance);
    }

    if (interval.overlap) {
      yValues.push(
        interval.support - OVERLAP_DISTANCE_PTS,
        interval.support + OVERLAP_DISTANCE_PTS,
      );
    }
  });

  const latestPrice = (
    pricePoints
      .filter((point) => point.t <= chartThrough)
      .at(-1)
      ?.price
    ?? null
  );

  const fallbackCenter = (
    Number.isFinite(latestPrice)
      ? latestPrice
      : Number.isFinite(currentUpdate?.support)
        ? currentUpdate.support
        : Number.isFinite(currentUpdate?.resistance)
          ? currentUpdate.resistance
          : 0
  );

  const rawLow = yValues.length
    ? Math.min(...yValues)
    : fallbackCenter - 10;

  const rawHigh = yValues.length
    ? Math.max(...yValues)
    : fallbackCenter + 10;

  const rawSpan = Math.max(5, rawHigh - rawLow);
  const yPad = Math.max(2.5, rawSpan * 0.12);

  const yFn = scaleLinear(
    [rawLow - yPad, rawHigh + yPad],
    [VH - MB, MT],
  );

  const chartPricePoints = displayPricePoints.filter(
    (point) => point.t <= chartThrough,
  );
  const visiblePricePoints = chartPricePoints.filter(
    (point) => (
      point.t >= visibleRange.from
      && point.t <= visibleRange.to
    ),
  );
  let firstVisibleIndex = 0;
  while (
    firstVisibleIndex < chartPricePoints.length
    && chartPricePoints[firstVisibleIndex].t < visibleRange.from
  ) {
    firstVisibleIndex += 1;
  }
  let lastVisibleIndex = chartPricePoints.length - 1;
  while (
    lastVisibleIndex >= 0
    && chartPricePoints[lastVisibleIndex].t > visibleRange.to
  ) {
    lastVisibleIndex -= 1;
  }
  const plotPricePoints = chartPricePoints.slice(
    Math.max(0, firstVisibleIndex - 1),
    Math.min(chartPricePoints.length, lastVisibleIndex + 2),
  );
  const latestVisiblePricePoint = visiblePricePoints.at(-1) ?? null;


  const yTickCount = clamp(
    Math.floor(plotHeight / 34),
    3,
    6,
  );

  const yTicks = Array.from(
    { length: yTickCount },
    (_, index) => {
      const fraction = (
        yTickCount === 1
          ? 0
          : index / (yTickCount - 1)
      );

      return (
        rawHigh + yPad
        - fraction * ((rawHigh + yPad) - (rawLow - yPad))
      );
    },
  );

  let timeTicks = [];
  const tickIntervalMs = 15 * 60 * 1000;
  if (visibleRange.to - visibleRange.from < tickIntervalMs) {
    timeTicks = [visibleRange.from, visibleRange.to];
  } else {
    const firstTick = Math.ceil(visibleRange.from / tickIntervalMs) * tickIntervalMs;
    for (let t = firstTick; t <= visibleRange.to; t += tickIntervalMs) {
      const minute = fmtAxisTime(t).slice(-2);

      if (TICK_MINUTES.has(minute)) {
        timeTicks.push(t);
      }
    }
  }
  timeTicks = [...new Set(timeTicks)].sort((a, b) => a - b);

  const nearestPricePoint = useCallback((targetTime) => {
    if (!visiblePricePoints.length) return null;

    let lo = 0;
    let hi = visiblePricePoints.length - 1;

    while (lo < hi) {
      const mid = Math.floor((lo + hi) / 2);

      if (visiblePricePoints[mid].t < targetTime) {
        lo = mid + 1;
      } else {
        hi = mid;
      }
    }

    const candidates = [lo, lo - 1]
      .filter(
        (index) => (
          index >= 0
          && index < visiblePricePoints.length
        ),
      );

    let best = null;
    let bestDistance = Infinity;

    candidates.forEach((index) => {
      const point = visiblePricePoints[index];
      const distance = Math.abs(point.t - targetTime);

      if (distance < bestDistance) {
        best = point;
        bestDistance = distance;
      }
    });

    return best;
  }, [visiblePricePoints]);

  const handlePointerMove = (event) => {
    const pan = panRef.current;
    if (pan) {
      const dx = event.clientX - pan.startX;
      if (pan.active || Math.abs(dx) >= PLOT_PAN_THRESHOLD_PX) {
        if (!pan.active) {
          pan.active = true;
          setIsPanning(true);
        }

        const shift = -(dx / plotWidth) * pan.visibleSpan;
        const sharedSpan = pan.startRange.to - pan.startRange.from;
        let nextFrom = pan.startRange.from + shift;
        let nextTo = nextFrom + sharedSpan;

        if (nextFrom < pan.domain.from) {
          nextFrom = pan.domain.from;
          nextTo = nextFrom + sharedSpan;
        }
        if (nextTo > pan.domain.to) {
          nextTo = pan.domain.to;
          nextFrom = nextTo - sharedSpan;
        }

        const panned = {
          from: Math.max(pan.domain.from, nextFrom),
          to: Math.min(pan.domain.to, nextTo),
        };
        viewDetachedRef.current = true;
        setSharedRangeEntry({ tradeDateKey, range: panned });
        schedulePublish(panned, { positionIsIntent: true });
      }
    }

    if (!visiblePricePoints.length) {
      setHover(null);
      clearFocusedMinute('eodRangeInsights');
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const mx = event.clientX - rect.left;
    const my = event.clientY - rect.top;

    if (
      mx < ML
      || mx > VW - MR
      || my < MT
      || my > VH - MB
    ) {
      setHover(null);
      clearFocusedMinute('eodRangeInsights');
      return;
    }

    const fraction = clamp(
      (mx - ML) / plotWidth,
      0,
      1,
    );

    const targetTime = (
      visibleRange.from
      + fraction * (visibleRange.to - visibleRange.from)
    );

    const point = nearestPricePoint(targetTime);

    if (!point) {
      setHover(null);
      clearFocusedMinute('eodRangeInsights');
      return;
    }

    setHover({
      t: point.t,
      price: point.price,
      x: xFn(point.t),
      y: yFn(point.price),
    });
    setFocusedMinute('eodRangeInsights', point.t);
  };

  const handlePointerDown = (event) => {
    if (
      event.button !== 0
      || !canPan
      || !domainMs
      || !(sharedRange?.to > sharedRange?.from)
    ) {
      return;
    }

    event.currentTarget.setPointerCapture?.(event.pointerId);
    panRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startRange: sharedRange,
      visibleSpan: visibleRange.to - visibleRange.from,
      domain: domainMs,
      preferenceMode,
      active: false,
    };
  };

  const finishPan = (event) => {
    if (
      !panRef.current
      || (
        event?.pointerId != null
        && event.pointerId !== panRef.current.pointerId
      )
    ) {
      return;
    }

    if (event?.currentTarget && event.pointerId != null) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    panRef.current = null;
    setIsPanning(false);
  };

  const synchronizedIndex = focusSourceId !== 'eodRangeInsights'
    ? focusedPointIndex(visiblePricePoints, focusedMinuteMs)
    : null;
  const synchronizedHover = synchronizedIndex == null ? null : {
    t: visiblePricePoints[synchronizedIndex].t,
    price: visiblePricePoints[synchronizedIndex].price,
    x: xFn(visiblePricePoints[synchronizedIndex].t),
    y: yFn(visiblePricePoints[synchronizedIndex].price),
  };
  const activeHover = hover ?? synchronizedHover;
  const displayPrice = hover?.price ?? latestVisiblePricePoint?.price ?? null;
  const displayTime = hover?.t ?? (
    visiblePricePoints.at(-1)?.t ?? null
  );

  const stateTone = railStateTone(currentUpdate);

  const currentOverlap = (
    currentUpdate
    && Number.isFinite(currentUpdate.support)
    && Number.isFinite(currentUpdate.resistance)
    && currentUpdate.support === currentUpdate.resistance
  );

  if (isBeforeWindow) {
    return (
      <div className="eodsm-panel">
        <div className="eodsm-empty">
          EOD Range activates at 14:30 ET
        </div>
      </div>
    );
  }

  return (
    <div className="eodsm-panel">
      <WidgetPanelStatus>
        {currentStateLabel(currentUpdate) && (
          <span className={`eodsm-state is-${stateTone}`}>
            {currentStateLabel(currentUpdate)}
          </span>
        )}
        {isAfterWindow && (
          <span className="eodsm-complete">
            COMPLETE
          </span>
        )}
      </WidgetPanelStatus>
      {!isAfterWindow && <WidgetPanelLiveStatus />}
      <WidgetPanelControls>
        <span className="eodsm-price">
          SPX {fmtPrice(displayPrice)}
        </span>

        <span
          className={[
            'eodsm-gts-context',
            currentGts?.side ? `is-${currentGts.side}` : 'is-neutral',
            currentGts?.value < GTS_CHALLENGE_THRESHOLD ? 'is-low' : '',
          ].filter(Boolean).join(' ')}
        >
          {gtsContextLabel(currentGts, currentUpdate)}
        </span>

        {displayTime && (
          <span className="eodsm-meta">
            {fmtTime(displayTime)}
          </span>
        )}

      </WidgetPanelControls>

      <div
        className="eodsm-wrap"
        ref={wrapRef}
      >
        <svg
          width={VW}
          height={VH}
          className={[
            'eodsm-chart',
            canPan ? 'can-pan' : '',
            isPanning ? 'is-panning' : '',
          ].filter(Boolean).join(' ')}
          onPointerMove={handlePointerMove}
          onPointerDown={handlePointerDown}
          onPointerUp={finishPan}
          onPointerCancel={finishPan}
          onPointerLeave={(event) => {
            if (!panRef.current) {
              setHover(null);
              clearFocusedMinute('eodRangeInsights');
            } else if (event.buttons === 0) {
              finishPan(event);
            }
          }}
          aria-label="SPX EOD Range chart"
        >
          <defs>
            <clipPath id={clipId}>
              <rect
                x={ML}
                y={MT}
                width={plotWidth}
                height={plotHeight}
              />
            </clipPath>
          </defs>
          {/* Y grid */}
          {yTicks.map((value) => (
            <React.Fragment key={`y:${value}`}>
              <line
                className="eodsm-grid"
                x1={ML}
                x2={VW - MR}
                y1={yFn(value)}
                y2={yFn(value)}
              />
              <text
                className="eodsm-axis"
                x={VW - MR + 5}
                y={yFn(value) + 3}
              >
                {fmtAxisPrice(value)}
              </text>
            </React.Fragment>
          ))}

          <g
            clipPath={`url(#${clipId})`}
            className={visibleRange.outside ? 'eodsm-plot is-outside' : 'eodsm-plot'}
            aria-label="EOD plot content"
          >
          {/* Current EOD structure shading for each backend interval.
              - Two-sided range: highlight the contained area between Support
                and Resistance with the same amber treatment used for overlap.
              - Resistance only (call side defined / put side open): shade the
                open downside below resistance in red.
              - Support only (put side defined / call side open): shade the
                open upside above support in green.
              Exact overlap is rendered separately below as the +/-10 zone. */}
          {intervals
            .filter((interval) => !interval.overlap)
            .map((interval) => {
              const hasSupport = Number.isFinite(interval.support);
              const hasResistance = Number.isFinite(interval.resistance);

              if (!hasSupport && !hasResistance) return null;

              const x1 = xFn(interval.t);
              const x2 = xFn(interval.end);
              const width = Math.max(1, x2 - x1);

              if (hasSupport && hasResistance) {
                const ySupport = yFn(interval.support);
                const yResistance = yFn(interval.resistance);

                return (
                  <rect
                    key={`range-zone:${interval.id}`}
                    className="eodsm-defined-range-zone"
                    x={x1}
                    y={Math.min(ySupport, yResistance)}
                    width={width}
                    height={Math.abs(ySupport - yResistance)}
                  />
                );
              }

              if (hasResistance) {
                const yResistance = yFn(interval.resistance);

                return (
                  <rect
                    key={`resistance-open-zone:${interval.id}`}
                    className="eodsm-resistance-open-zone"
                    x={x1}
                    y={Math.min(yResistance, VH - MB)}
                    width={width}
                    height={Math.max(0, (VH - MB) - yResistance)}
                  />
                );
              }

              const ySupport = yFn(interval.support);

              return (
                <rect
                  key={`support-open-zone:${interval.id}`}
                  className="eodsm-support-open-zone"
                  x={x1}
                  y={MT}
                  width={width}
                  height={Math.max(0, ySupport - MT)}
                />
              );
            })}

          {/* Historical/current overlap intervals. The +/-10 zone ends exactly
              when the next EOD post separates Support and Resistance. */}
          {intervals
            .filter((interval) => interval.overlap)
            .map((interval) => {
              const x1 = xFn(interval.t);
              const x2 = xFn(interval.end);
              const yTop = yFn(
                interval.support + OVERLAP_DISTANCE_PTS,
              );
              const yBottom = yFn(
                interval.support - OVERLAP_DISTANCE_PTS,
              );

              return (
                <g key={`overlap:${interval.id}`}>
                  <rect
                    className="eodsm-overlap-zone"
                    x={x1}
                    y={Math.min(yTop, yBottom)}
                    width={Math.max(1, x2 - x1)}
                    height={Math.abs(yBottom - yTop)}
                  />
                  <line
                    className="eodsm-overlap-line"
                    x1={x1}
                    x2={x2}
                    y1={yFn(interval.support)}
                    y2={yFn(interval.support)}
                  />
                </g>
              );
            })}

          {/* Support rail intervals. Hidden while exact overlap is active. */}
          {intervals
            .filter(
              (interval) => (
                !interval.overlap
                && Number.isFinite(interval.support)
              ),
            )
            .map((interval) => (
              <line
                key={`support:${interval.id}`}
                className="eodsm-support-rail"
                x1={xFn(interval.t)}
                x2={xFn(interval.end)}
                y1={yFn(interval.support)}
                y2={yFn(interval.support)}
              />
            ))}

          {/* Resistance rail intervals. Hidden while exact overlap is active. */}
          {intervals
            .filter(
              (interval) => (
                !interval.overlap
                && Number.isFinite(interval.resistance)
              ),
            )
            .map((interval) => (
              <line
                key={`resistance:${interval.id}`}
                className="eodsm-resistance-rail"
                x1={xFn(interval.t)}
                x2={xFn(interval.end)}
                y1={yFn(interval.resistance)}
                y2={yFn(interval.resistance)}
              />
            ))}

          {/* Directional GTS challenge context.
              Elevated CALL GTS overlays only Resistance.
              Elevated PUT GTS overlays only Support.
              The base EOD rails remain authoritative. */}
          {gtsChallengeSegments.map((segment) => {
            const strength = clamp(
              (segment.value - GTS_CHALLENGE_THRESHOLD)
                / Math.max(
                  0.01,
                  GTS_MAX_CONTEXT - GTS_CHALLENGE_THRESHOLD,
                ),
              0,
              1,
            );

            const offset = segment.overlap
              ? (segment.side === 'call' ? -2 : 2)
              : 0;

            return (
              <line
                key={segment.key}
                className={[
                  'eodsm-gts-challenge-rail',
                  `is-${segment.side}`,
                ].join(' ')}
                x1={xFn(segment.t)}
                x2={xFn(segment.end)}
                y1={yFn(segment.level) + offset}
                y2={yFn(segment.level) + offset}
                style={{
                  opacity: 0.48 + (strength * 0.42),
                  strokeWidth: 1.25 + (strength * 1.15),
                }}
              />
            );
          })}

          {/* Rail migration markers at each backend update. */}
          {eodUpdates
            .filter((update) => update.t <= chartThrough)
            .map((update) => {
              const overlap = (
                Number.isFinite(update.support)
                && Number.isFinite(update.resistance)
                && update.support === update.resistance
              );

              if (overlap) {
                return (
                  <g key={`update:${update.id}`}>
                    <circle
                      className="eodsm-overlap-marker"
                      cx={xFn(update.t)}
                      cy={yFn(update.support)}
                      r="3.2"
                    />
                    <text
                      className="eodsm-level-label is-overlap"
                      x={xFn(update.t) + 5}
                      y={yFn(update.support) - 5}
                    >
                      {fmtStrike(update.support)}
                    </text>
                  </g>
                );
              }

              return (
                <g key={`update:${update.id}`}>
                  {Number.isFinite(update.support) && (
                    <>
                      <circle
                        className="eodsm-support-marker"
                        cx={xFn(update.t)}
                        cy={yFn(update.support)}
                        r="2.4"
                      />
                      <text
                        className="eodsm-level-label is-support"
                        x={xFn(update.t) + 4}
                        y={yFn(update.support) - 4}
                      >
                        {fmtStrike(update.support)}
                      </text>
                    </>
                  )}

                  {Number.isFinite(update.resistance) && (
                    <>
                      <circle
                        className="eodsm-resistance-marker"
                        cx={xFn(update.t)}
                        cy={yFn(update.resistance)}
                        r="2.4"
                      />
                      <text
                        className="eodsm-level-label is-resistance"
                        x={xFn(update.t) + 4}
                        y={yFn(update.resistance) + 10}
                      >
                        {fmtStrike(update.resistance)}
                      </text>
                    </>
                  )}
                </g>
              );
            })}

          {/* SPX price path. Hydrated minute closes seed the chart; every live
              tick after connection is retained at streaming resolution. */}
          <polyline
            className="eodsm-price-line"
            aria-label="SPX price path"
            points={plotPricePoints.map((point) => `${xFn(point.t)},${yFn(point.price)}`).join(' ')}
            fill="none"
          />

          {latestVisiblePricePoint && (
            <circle
              className="eodsm-price-dot"
              aria-label="SPX visible price"
              cx={xFn(latestVisiblePricePoint.t)}
              cy={yFn(latestVisiblePricePoint.price)}
              r="2.8"
            />
          )}

          {/* Current overlap annotation */}
          {currentOverlap && (
            <text
              className="eodsm-overlap-caption"
              x={VW - MR - 5}
              y={Math.max(
                MT + 10,
                yFn(currentUpdate.support + OVERLAP_DISTANCE_PTS) + 11,
              )}
              textAnchor="end"
            >
              ±{OVERLAP_DISTANCE_PTS} OVERLAP ZONE
            </text>
          )}
          </g>

          {/* Fixed 14:30–16:00 time axis */}
          {timeTicks.map((t, index) => (
            <React.Fragment key={`time:${t}`}>
              <line
                className="eodsm-time-grid"
                x1={xFn(t)}
                x2={xFn(t)}
                y1={MT}
                y2={VH - MB}
              />
              <text
                className="eodsm-axis"
                x={xFn(t)}
                y={VH - 5}
                textAnchor={
                  index === 0
                    ? 'start'
                    : index === timeTicks.length - 1
                      ? 'end'
                      : 'middle'
                }
              >
                {fmtAxisTime(t)}
              </text>
            </React.Fragment>
          ))}

          {/* Hover crosshair */}
          {activeHover && (
            <>
              <line
                className="eodsm-crosshair"
                x1={activeHover.x}
                x2={activeHover.x}
                y1={MT}
                y2={VH - MB}
              />
              {hover && (
                <>
                  <line
                    className="eodsm-crosshair"
                    x1={ML}
                    x2={VW - MR}
                    y1={hover.y}
                    y2={hover.y}
                  />
                  <circle
                    className="eodsm-hover-dot"
                    cx={hover.x}
                    cy={hover.y}
                    r="3"
                  />
                </>
              )}
            </>
          )}
        </svg>

        {visibleRange.outside && (
          <div className="eodsm-outside-view">
            Shared view is outside the EOD window
          </div>
        )}

        {!pricePoints.length && !isAfterWindow && (
          <div className="eodsm-overlay-empty">
            Waiting for EOD data...
          </div>
        )}

        {pricePoints.length > 0 && !eodUpdates.length && !isAfterWindow && (
          <div className="eodsm-overlay-empty">
            Waiting for EOD data...
          </div>
        )}
      </div>
    </div>
  );
}

export {
  OVERLAP_DISTANCE_PTS,
  GTS_CHALLENGE_THRESHOLD,
  eodVisibleRange,
  parseStrike,
};
